import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { authService } from '../../services/authService';

const { 
  FiUser, FiLock, FiSave, FiEye, FiEyeOff, FiRefreshCw, FiWifi, FiWifiOff, 
  FiActivity, FiCheckCircle, FiAlertCircle, FiClock 
} = FiIcons;

const ProfileManagement = () => {
  const [activeTab, setActiveTab] = useState('profile');
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [isConnected, setIsConnected] = useState(false);
  const [profileSubscription, setProfileSubscription] = useState(null);
  const [saveStatus, setSaveStatus] = useState('idle'); // 'idle', 'saving', 'saved', 'error'

  // Profile form state
  const [profileData, setProfileData] = useState({
    fullName: '',
    email: '',
    username: ''
  });

  // Password form state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  useEffect(() => {
    const user = authService.getCurrentUser();
    if (user) {
      setCurrentUser(user);
      setProfileData({
        fullName: user.fullName || '',
        email: user.email || '',
        username: user.username || ''
      });

      // Setup real-time subscription for profile changes
      setupRealtimeSubscription(user.id);
    }

    // Initial connection test
    testConnection();

    // Setup periodic connection monitoring
    const connectionInterval = setInterval(monitorConnection, 30000); // Check every 30 seconds

    // Cleanup
    return () => {
      if (profileSubscription) {
        profileSubscription.unsubscribe();
      }
      clearInterval(connectionInterval);
    };
  }, []);

  const setupRealtimeSubscription = (userId) => {
    try {
      const subscription = authService.subscribeToProfileChanges(
        userId,
        (updatedProfile) => {
          // Update current user state
          setCurrentUser(prev => ({
            ...prev,
            fullName: updatedProfile.full_name,
            email: updatedProfile.email
          }));
          
          // Update profile form data
          setProfileData(prev => ({
            ...prev,
            fullName: updatedProfile.full_name,
            email: updatedProfile.email
          }));
          
          // Show real-time sync notification
          setMessage({ 
            type: 'success', 
            text: '✅ Profile updated successfully!' 
          });
          
          // Auto-clear message after 3 seconds
          setTimeout(() => {
            setMessage({ type: '', text: '' });
          }, 3000);
        }
      );

      setProfileSubscription(subscription);
    } catch (error) {
      console.error('Failed to setup real-time subscription:', error);
    }
  };

  const testConnection = async () => {
    try {
      const result = await authService.testConnection();
      setIsConnected(result.success);
      
      if (!result.success) {
        setMessage({ 
          type: 'error', 
          text: 'Connection issue detected. Some features may be limited.' 
        });
      }
    } catch (error) {
      setIsConnected(false);
    }
  };

  const monitorConnection = async () => {
    try {
      const status = await authService.monitorConnection();
      setIsConnected(status.connected);
      
      if (!status.connected && message.type !== 'error') {
        setMessage({ 
          type: 'error', 
          text: 'Connection temporarily unavailable. Changes will be saved when reconnected.' 
        });
      }
    } catch (error) {
      setIsConnected(false);
    }
  };

  const refreshProfile = async () => {
    if (!currentUser) return;

    setIsLoading(true);
    setSaveStatus('saving');
    
    try {
      const result = await authService.getProfile(currentUser.id);
      
      if (result.success) {
        const updatedUser = {
          ...currentUser,
          fullName: result.user.full_name,
          email: result.user.email
        };
        
        setCurrentUser(updatedUser);
        setProfileData({
          fullName: result.user.full_name || '',
          email: result.user.email || '',
          username: result.user.username || ''
        });
        
        setSaveStatus('saved');
        setMessage({ 
          type: 'success', 
          text: '✅ Profile refreshed successfully!' 
        });
        
        await testConnection();
      } else {
        setSaveStatus('error');
        setMessage({ 
          type: 'error', 
          text: `Failed to refresh profile: ${result.error}` 
        });
      }
    } catch (error) {
      setSaveStatus('error');
      setMessage({ 
        type: 'error', 
        text: `Failed to refresh profile: ${error.message}` 
      });
    } finally {
      setIsLoading(false);
      
      setTimeout(() => {
        setSaveStatus('idle');
      }, 3000);
    }
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    if (!currentUser) return;

    setIsLoading(true);
    setSaveStatus('saving');
    setMessage({ type: '', text: '' });

    try {
      const result = await authService.updateProfile(currentUser.id, {
        fullName: profileData.fullName,
        email: profileData.email
      });

      if (result.success) {
        setSaveStatus('saved');
        setMessage({ 
          type: 'success', 
          text: '✅ Profile updated successfully!' 
        });
        
        // Update current user immediately
        const updatedUser = authService.getCurrentUser();
        if (updatedUser) {
          setCurrentUser(updatedUser);
        }
        
        await testConnection();
        
        setTimeout(() => {
          setMessage({ type: '', text: '' });
        }, 5000);
      } else {
        setSaveStatus('error');
        setMessage({ 
          type: 'error', 
          text: `Failed to update profile: ${result.error}` 
        });
      }
    } catch (error) {
      setSaveStatus('error');
      setMessage({ 
        type: 'error', 
        text: `An unexpected error occurred: ${error.message}` 
      });
    } finally {
      setIsLoading(false);
      
      setTimeout(() => {
        setSaveStatus('idle');
      }, 3000);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!currentUser) return;

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setMessage({ type: 'error', text: 'New password must be at least 6 characters long' });
      return;
    }

    setIsLoading(true);
    setSaveStatus('saving');
    setMessage({ type: '', text: '' });

    try {
      const result = await authService.changePassword(
        currentUser.id,
        passwordData.currentPassword,
        passwordData.newPassword
      );

      if (result.success) {
        setSaveStatus('saved');
        setMessage({ 
          type: 'success', 
          text: '✅ Password changed successfully!' 
        });
        
        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
        
        await testConnection();
        
        setTimeout(() => {
          setMessage({ type: '', text: '' });
        }, 5000);
      } else {
        setSaveStatus('error');
        setMessage({ 
          type: 'error', 
          text: `Failed to change password: ${result.error}` 
        });
      }
    } catch (error) {
      setSaveStatus('error');
      setMessage({ 
        type: 'error', 
        text: `An unexpected error occurred: ${error.message}` 
      });
    } finally {
      setIsLoading(false);
      
      setTimeout(() => {
        setSaveStatus('idle');
      }, 3000);
    }
  };

  const getSaveStatusIcon = () => {
    switch (saveStatus) {
      case 'saving':
        return FiClock;
      case 'saved':
        return FiCheckCircle;
      case 'error':
        return FiAlertCircle;
      default:
        return FiSave;
    }
  };

  const getSaveStatusColor = () => {
    switch (saveStatus) {
      case 'saving':
        return 'text-blue-600';
      case 'saved':
        return 'text-green-600';
      case 'error':
        return 'text-red-600';
      default:
        return 'text-white';
    }
  };

  const tabs = [
    { id: 'profile', name: 'Profile Information', icon: FiUser },
    { id: 'password', name: 'Change Password', icon: FiLock }
  ];

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="h-10 w-10 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-lg flex items-center justify-center mr-4">
              <SafeIcon icon={FiActivity} className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Profile Management</h1>
              <p className="text-gray-600 mt-1">Best Podiatrist - Dr. Amit Kumar C Jain Admin Settings</p>
            </div>
          </div>
          
          {/* Connection Status & Controls */}
          <div className="flex items-center space-x-3">
            {/* Real-time sync indicator */}
            <div className="flex items-center space-x-2">
              <div className={`h-2 w-2 rounded-full ${
                isConnected ? 'bg-green-400 animate-pulse' : 'bg-red-400'
              }`}></div>
              <span className="text-xs text-gray-500">
                {isConnected ? 'Online' : 'Offline'}
              </span>
            </div>
            
            {/* Connection Status */}
            <div className={`flex items-center px-3 py-1 rounded-full text-sm ${
              isConnected 
                ? 'bg-green-100 text-green-700' 
                : 'bg-red-100 text-red-700'
            }`}>
              <SafeIcon 
                icon={isConnected ? FiWifi : FiWifiOff} 
                className="h-4 w-4 mr-1" 
              />
              {isConnected ? 'Connected' : 'Offline'}
            </div>
            
            {/* Refresh Button */}
            <motion.button
              onClick={refreshProfile}
              disabled={isLoading}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
              title="Refresh profile"
            >
              <SafeIcon 
                icon={FiRefreshCw} 
                className={`h-5 w-5 ${isLoading ? 'animate-spin' : ''}`} 
              />
            </motion.button>
          </div>
        </div>
      </div>

      {/* Message Display */}
      {message.text && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`mb-6 p-4 rounded-lg border-l-4 ${
            message.type === 'success' 
              ? 'bg-green-50 border-green-400 text-green-700' 
              : 'bg-red-50 border-red-400 text-red-700'
          }`}
        >
          <div className="flex items-center">
            <SafeIcon 
              icon={message.type === 'success' ? FiCheckCircle : FiAlertCircle} 
              className="h-5 w-5 mr-2" 
            />
            {message.text}
          </div>
        </motion.div>
      )}

      {/* Connection Warning */}
      {!isConnected && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg"
        >
          <div className="flex items-center">
            <SafeIcon icon={FiWifiOff} className="h-5 w-5 text-yellow-600 mr-2" />
            <div>
              <p className="text-yellow-800 font-medium">Connection Issue</p>
              <p className="text-yellow-700 text-sm">
                Some features may be limited. Changes will be saved locally and synced when connection is restored.
              </p>
            </div>
            <button 
              onClick={testConnection}
              className="ml-auto px-3 py-1 bg-yellow-600 text-white text-sm rounded hover:bg-yellow-700 transition-colors"
            >
              Retry
            </button>
          </div>
        </motion.div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id);
                setMessage({ type: '', text: '' });
              }}
              className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <SafeIcon icon={tab.icon} className="mr-2 h-4 w-4" />
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Profile Information Tab */}
      {activeTab === 'profile' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow p-6"
        >
          <form onSubmit={handleProfileSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name
                </label>
                <input
                  type="text"
                  value={profileData.fullName}
                  onChange={(e) => setProfileData({ ...profileData, fullName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  value={profileData.email}
                  onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Username
                </label>
                <input
                  type="text"
                  value={profileData.username}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 cursor-not-allowed"
                  disabled
                />
                <p className="text-xs text-gray-500 mt-1">Username cannot be changed</p>
              </div>
            </div>

            {/* Sync Information */}
            <div className="bg-gradient-to-r from-blue-50 to-green-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <SafeIcon icon={FiCheckCircle} className="h-5 w-5 text-blue-600 mt-0.5" />
                <div>
                  <p className="text-sm text-blue-800 font-medium">
                    Best Podiatrist Profile Management
                  </p>
                  <p className="text-sm text-blue-700 mt-1">
                    • Changes are automatically saved<br/>
                    • Secure profile management for Dr. Amit Kumar C Jain's practice<br/>
                    • {isConnected ? '✅ Online & Syncing' : '⚠️ Offline Mode - Will sync when connected'}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <motion.button
                type="submit"
                disabled={isLoading}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className={`flex items-center px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 ${
                  saveStatus === 'saved' 
                    ? 'bg-green-600 hover:bg-green-700 text-white'
                    : saveStatus === 'error'
                    ? 'bg-red-600 hover:bg-red-700 text-white'
                    : 'bg-blue-600 hover:bg-blue-700 text-white'
                }`}
              >
                <SafeIcon 
                  icon={getSaveStatusIcon()} 
                  className={`mr-2 h-4 w-4 ${
                    saveStatus === 'saving' ? 'animate-spin' : ''
                  } ${getSaveStatusColor()}`} 
                />
                {saveStatus === 'saving' ? 'Saving...' : 
                 saveStatus === 'saved' ? 'Saved Successfully!' :
                 saveStatus === 'error' ? 'Save Failed - Retry' :
                 'Save Changes'}
              </motion.button>
            </div>
          </form>
        </motion.div>
      )}

      {/* Change Password Tab */}
      {activeTab === 'password' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow p-6"
        >
          <form onSubmit={handlePasswordSubmit} className="space-y-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Current Password
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.current ? 'text' : 'password'}
                    value={passwordData.currentPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    <SafeIcon 
                      icon={showPasswords.current ? FiEyeOff : FiEye} 
                      className="h-4 w-4 text-gray-400 hover:text-gray-600" 
                    />
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Password
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.new ? 'text' : 'password'}
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                    minLength="6"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    <SafeIcon 
                      icon={showPasswords.new ? FiEyeOff : FiEye} 
                      className="h-4 w-4 text-gray-400 hover:text-gray-600" 
                    />
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Confirm New Password
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.confirm ? 'text' : 'password'}
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                    minLength="6"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    <SafeIcon 
                      icon={showPasswords.confirm ? FiEyeOff : FiEye} 
                      className="h-4 w-4 text-gray-400 hover:text-gray-600" 
                    />
                  </button>
                </div>
              </div>
            </div>

            {/* Security Information */}
            <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <SafeIcon icon={FiLock} className="h-5 w-5 text-yellow-600 mt-0.5" />
                <div>
                  <p className="text-sm text-yellow-800 font-medium">
                    Password Security for Best Podiatrist
                  </p>
                  <ul className="text-sm text-yellow-700 mt-1 space-y-1">
                    <li>• Minimum 6 characters long</li>
                    <li>• Changes are immediately saved</li>
                    <li>• Secure access to Dr. Amit Kumar C Jain's practice management</li>
                    <li>• {isConnected ? '✅ Secure connection active' : '⚠️ Offline - Will sync securely when connected'}</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <motion.button
                type="submit"
                disabled={isLoading}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className={`flex items-center px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 ${
                  saveStatus === 'saved' 
                    ? 'bg-green-600 hover:bg-green-700 text-white'
                    : saveStatus === 'error'
                    ? 'bg-red-600 hover:bg-red-700 text-white'
                    : 'bg-blue-600 hover:bg-blue-700 text-white'
                }`}
              >
                <SafeIcon 
                  icon={getSaveStatusIcon()} 
                  className={`mr-2 h-4 w-4 ${
                    saveStatus === 'saving' ? 'animate-spin' : ''
                  } ${getSaveStatusColor()}`} 
                />
                {saveStatus === 'saving' ? 'Updating...' : 
                 saveStatus === 'saved' ? 'Password Updated!' :
                 saveStatus === 'error' ? 'Update Failed - Retry' :
                 'Change Password'}
              </motion.button>
            </div>
          </form>
        </motion.div>
      )}

      {/* Footer */}
      <div className="mt-8 pt-6 border-t border-gray-200 text-center">
        <div className="flex items-center justify-center space-x-4 text-sm text-gray-500">
          <span>Best Podiatrist Copyright 2025. Dr Amit Kumar C Jain ( Diabetic Foot Surgeon). All rights Reserved.</span>
          <div className="flex items-center space-x-2">
            <div className={`h-2 w-2 rounded-full ${isConnected ? 'bg-green-400' : 'bg-red-400'}`}></div>
            <span className={isConnected ? 'text-green-600' : 'text-red-600'}>
              {isConnected ? 'Online' : 'Offline'}
            </span>
          </div>
        </div>
        <p className="text-xs text-gray-400 mt-1">
          Best Podiatrist Practice Management System
        </p>
      </div>
    </div>
  );
};

export default ProfileManagement;